/**
 * Profiles Schema
 * Core user profile table - mirrors auth.users extension
 */

import { pgTable, uuid, varchar, text, timestamp } from 'drizzle-orm/pg-core';
import { relations } from 'drizzle-orm';

// =============================================================================
// Profiles Table
// =============================================================================
export const profiles = pgTable('profiles', {
  id: uuid('id').primaryKey(), // References auth.users.id
  username: varchar('username', { length: 255 }),
  fullName: varchar('full_name', { length: 255 }),
  avatarUrl: text('avatar_url'),
  website: text('website'),
  company: varchar('company', { length: 255 }),
  role: varchar('role', { length: 50 }),
  // Stripe customer ID for payment processing
  stripeCustomerId: varchar('stripe_customer_id', { length: 255 }),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// User Roles Table
// =============================================================================
export const userRoles = pgTable('user_roles', {
  id: uuid('id').primaryKey().defaultRandom(),
  userId: uuid('user_id').notNull(),
  role: varchar('role', { length: 50 }).notNull().default('standard'), // 'standard' | 'super_user'
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// User Preferences Table
// =============================================================================
export const userPreferences = pgTable('user_preferences', {
  id: uuid('id').primaryKey().defaultRandom(),
  userId: uuid('user_id').notNull().unique(),
  preferences: text('preferences').default('{}'), // JSON stored as text
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// =============================================================================
// Relations
// =============================================================================
export const profilesRelations = relations(profiles, ({ many }) => ({
  roles: many(userRoles),
  preferences: many(userPreferences),
}));

export const userRolesRelations = relations(userRoles, ({ one }) => ({
  profile: one(profiles, {
    fields: [userRoles.userId],
    references: [profiles.id],
  }),
}));

export const userPreferencesRelations = relations(userPreferences, ({ one }) => ({
  profile: one(profiles, {
    fields: [userPreferences.userId],
    references: [profiles.id],
  }),
}));

// =============================================================================
// Types
// =============================================================================
export type Profile = typeof profiles.$inferSelect;
export type NewProfile = typeof profiles.$inferInsert;
export type UserRole = typeof userRoles.$inferSelect;
export type NewUserRole = typeof userRoles.$inferInsert;
export type UserPreference = typeof userPreferences.$inferSelect;
export type NewUserPreference = typeof userPreferences.$inferInsert;
